import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/api';
import { formatDateTimeSaudi } from '../../utils/saudiTime';

function isAllowedRole(role) {
  const r = String(role || '').toLowerCase();
  return r === 'admin' || r === 'master_admin' || r === 'custom_role_order_receiver';
}

export default function CashHandover() {
  const { user } = useAuth();
  const role = user?.role;

  const [salesUsers, setSalesUsers] = useState([]);
  const [salesUsersLoading, setSalesUsersLoading] = useState(true);
  const [salesUsersError, setSalesUsersError] = useState('');

  const [selectedSalesUserId, setSelectedSalesUserId] = useState('');
  const [today, setToday] = useState(null);
  const [todayLoading, setTodayLoading] = useState(false);
  const [todayError, setTodayError] = useState('');

  const [cashInHand, setCashInHand] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function loadSalesUsers() {
      setSalesUsersError('');
      setSalesUsersLoading(true);
      try {
        const res = await apiService.users.listSalesUsers();
        if (cancelled) return;
        const rows = Array.isArray(res.data) ? res.data : [];
        setSalesUsers(rows);
      } catch (err) {
        if (!cancelled) setSalesUsersError(err.response?.data?.error || err.message || 'Failed to load sales users');
      } finally {
        if (!cancelled) setSalesUsersLoading(false);
      }
    }
    loadSalesUsers();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadToday() {
      if (!selectedSalesUserId) return;
      setTodayError('');
      setTodayLoading(true);
      setSubmitSuccess('');
      try {
        const res = await apiService.cashHandover.getToday(selectedSalesUserId);
        if (cancelled) return;
        setToday(res.data || null);
        const existing = res.data?.existingHandover;
        if (existing && existing.cashInHand != null) {
          setCashInHand(String(existing.cashInHand));
        } else {
          setCashInHand('');
        }
      } catch (err) {
        if (!cancelled) setTodayError(err.response?.data?.error || err.message || 'Failed to load today data');
        setToday(null);
      } finally {
        if (!cancelled) setTodayLoading(false);
      }
    }
    loadToday();
    return () => {
      cancelled = true;
    };
  }, [selectedSalesUserId]);

  const cashFromOrders = Number(today?.cashFromOrders || 0);
  const unpaidCount = Number(today?.unpaidCount || 0);
  const existing = today?.existingHandover || null;

  const cashInHandNumber = useMemo(() => {
    const n = Number(String(cashInHand || '').trim());
    return Number.isFinite(n) ? n : NaN;
  }, [cashInHand]);

  const variance = useMemo(() => {
    if (!Number.isFinite(cashInHandNumber)) return null;
    return cashFromOrders - cashInHandNumber;
  }, [cashFromOrders, cashInHandNumber]);

  const canSubmit =
    Boolean(selectedSalesUserId) &&
    today &&
    unpaidCount === 0 &&
    Number.isFinite(cashInHandNumber) &&
    cashInHandNumber >= 0 &&
    !submitting;

  const onSubmit = async () => {
    if (!canSubmit) return;
    setSubmitError('');
    setSubmitSuccess('');
    setSubmitting(true);
    try {
      const res = await apiService.cashHandover.submit({
        salesUserId: selectedSalesUserId,
        cashInHand: cashInHandNumber,
      });
      setToday((prev) => (prev ? { ...prev, existingHandover: res.data } : prev));
      setSubmitSuccess('Hand over saved.');
    } catch (err) {
      setSubmitError(err.response?.data?.error || err.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isAllowedRole(role)) {
    return (
      <div className="p-3 sm:p-4 md:p-6">
        <div className="bg-white rounded-lg shadow-md border-l-4 border-red-500 p-4">
          <h1 className="text-xl font-bold text-gray-700 mb-1">Cash handover</h1>
          <p className="text-sm text-gray-600">Access denied.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6">
      <div className="bg-white rounded-lg shadow-md border-l-4 border-blue-500 p-3 sm:p-4 md:p-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-700 mb-2">Cash handover</h1>
        <p className="text-sm text-gray-600 mb-4">
          Select a sales user to calculate today&apos;s paid revenue and hand over cash.
        </p>

        {salesUsersError && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
            {salesUsersError}
          </div>
        )}

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="sales-user">
            Sales user
          </label>
          <select
            id="sales-user"
            value={selectedSalesUserId}
            onChange={(e) => setSelectedSalesUserId(e.target.value)}
            disabled={salesUsersLoading}
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-gray-700 focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <option value="">{salesUsersLoading ? 'Loading…' : 'Select sales user'}</option>
            {salesUsers.map((u) => (
              <option key={u._id} value={u._id}>
                {u.name}
              </option>
            ))}
          </select>
        </div>

        {todayError && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
            {todayError}
          </div>
        )}

        {todayLoading && <div className="py-6 text-center text-gray-500">Loading…</div>}

        {!todayLoading && today && (
          <>
            {unpaidCount > 0 && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-900 rounded-lg text-sm">
                There are <span className="font-bold">{unpaidCount}</span> unpaid order(s) today. You cannot hand over cash
                until all payments are received.
              </div>
            )}

            {submitError && (
              <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
                {submitError}
              </div>
            )}
            {submitSuccess && (
              <div className="mb-4 p-3 bg-green-100 border border-green-300 text-green-800 rounded-lg text-sm">
                {submitSuccess}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div className="text-xs text-gray-500">Cash from orders (paid today)</div>
                <div className="text-xl font-bold text-gray-800 tabular-nums">SAR {cashFromOrders.toFixed(2)}</div>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <label className="block text-xs text-gray-500 mb-1" htmlFor="cash-in-hand">
                  Cash in hand (manual)
                </label>
                <input
                  id="cash-in-hand"
                  type="number"
                  min="0"
                  step="0.01"
                  value={cashInHand}
                  onChange={(e) => setCashInHand(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-700 focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="mb-4 bg-white border border-gray-200 rounded-lg p-3">
              <div className="text-xs text-gray-500">Variance (cash from orders - cash in hand)</div>
              <div className="text-xl font-bold text-gray-800 tabular-nums">
                {variance == null ? '—' : `SAR ${Number(variance).toFixed(2)}`}
              </div>
            </div>

            {existing?.submittedAt && (
              <div className="mb-4 text-sm text-gray-600">
                Last handover: <span className="font-medium">{formatDateTimeSaudi(existing.submittedAt)}</span>
                {existing.submittedByName ? <span> · by {existing.submittedByName}</span> : null}
              </div>
            )}

            <button
              type="button"
              onClick={onSubmit}
              disabled={!canSubmit}
              className="w-full sm:w-auto px-4 py-2.5 rounded-lg bg-[#735dff] text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
            >
              {submitting ? 'Submitting…' : 'Hand over (submit)'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

